import { memo, useState, useEffect, useCallback } from "react";

import {
  DataGridPremium,
  GridColumns,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-premium";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import { useIgnoreStore } from "stores/IgnoreStore";
import { ActionState } from "stores/lib";

import {
  generateLoadingColumns,
  generateLoadingRows,
} from "components/Table/Loading";

import { RuleManagementTableColumns, TableRule, getTableData } from "./data";

// -----------------------------------------------------------------------------

const RuleTable = memo(function RuleTable({
  searchTerm,
}: RuleTableProps): JSX.Element {
  const [ignoreStore, ignoreActions] = useIgnoreStore();

  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<TableRule[]>([]);

  const LoadingColumns: GridColumns = generateLoadingColumns(
    RuleManagementTableColumns
  );
  const LoadingRows: any = generateLoadingRows(10, "uuid");

  useEffect(() => {
    ignoreActions.listIgnoreRules();
  }, [ignoreActions]);

  useEffect(() => {
    if (ignoreStore.listRulesState === ActionState.COMPLETED) {
      setLoading(false);
      const rules = ignoreStore.ignoreRules.map((rule) => getTableData(rule));
      const treatedFilterText = searchTerm.trim();
      const filteredRules = treatedFilterText
        ? rules.filter((rule) =>
            Object.entries(rule).some(([k, v]) => {
              if (k === "created_at") return false;
              return v.toLowerCase().includes(treatedFilterText.toLowerCase());
            })
          )
        : rules;
      setRows(filteredRules);
    }
  }, [ignoreStore, ignoreActions, searchTerm]);

  const getDetailPanelContent = useCallback(({ row }: { row: TableRule }) => {
    if (!row.ignore_reason) return null;
    return (
      <Box sx={{ ml: 7.5 }}>
        <Typography variant="body1" sx={{ fontSize: "14px", mt: 2 }}>
          Ignore reason:
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "14px", mb: 2 }}>
          {row.ignore_reason}
        </Typography>
      </Box>
    );
  }, []);

  return (
    <Paper
      elevation={1}
      sx={{
        p: 0,
        width: "100%",
        height: "100%",
        minHeight: 512,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGridPremium
        getRowId={(row: TableRule) => row.uuid}
        columns={loading ? LoadingColumns : RuleManagementTableColumns}
        rows={loading ? LoadingRows : rows}
        loading={loading}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20]}
        pagination
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
        disableSelectionOnClick
        // Workaround for console warning
        columnTypes={{
          [GRID_DETAIL_PANEL_TOGGLE_COL_DEF.type as string]:
            GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-cell": {
            wordBreak: "break-all",

            "&:focus": {
              outline: "none",
            },
          },

          "& .MuiDataGrid-columnHeader": {
            "&:focus": {
              outline: "none",
            },
          },

          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: 0,
          },

          "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator":
            {
              display: "none",
            },
        }}
      />
    </Paper>
  );
});

export default RuleTable;

// -----------------------------------------------------------------------------

interface RuleTableProps {
  searchTerm: string;
}
